import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { normaliseBrand } from "@/lib/brandRegistry";
import {
  autoCaptureToWardrobe,
  undoWardrobeAdd,
} from "@/lib/wardrobeAutoCapture";


const CORAL = "var(--l2f-coral)";
const CREAM = "var(--l2f-cream)";

type Props = {
  outfitId: string;
  itemName: string;
  itemCategory: string;
  retailer: string | null;
  productUrl: string | null;
  detectedItemId: string | null;
  onDismiss: () => void;
  onComplete: () => void;
};

type Step = "purchased" | "fit" | "done";

const updateBrandFit = async (
  userId: string,
  category: string,
  retailer: string,
  fitOutcome: string,
) => {
  const isTop = ["top", "outerwear", "dress"].includes(category.toLowerCase());
  const field = isTop ? "brand_fit_tops" : "brand_fit_bottoms";
  const normalisedRetailer = normaliseBrand(retailer) ?? retailer;

  const { data: profile } = await (supabase.from("profiles") as any)
    .select(field)
    .eq("id", userId)
    .single();

  const existing: any[] = (profile as any)?.[field] ?? [];

  const reliability =
    fitOutcome === "perfect"
      ? "high"
      : ["returned", "too_tight", "too_loose"].includes(fitOutcome)
        ? "low"
        : "medium";

  const verdict =
    reliability === "high"
      ? "true_to_size"
      : reliability === "low"
        ? "runs_small"
        : "true_to_size";

  const idx = existing.findIndex((b: any) => {
    const storedNormalised = normaliseBrand(b.brand ?? b.retailer ?? "");
    return storedNormalised && storedNormalised === normalisedRetailer;
  });

  const updated = [...existing];
  const today = new Date().toISOString().split("T")[0];

  if (idx >= 0) {
    updated[idx] = {
      ...updated[idx],
      brand: normalisedRetailer,
      fit_reliability: reliability,
      verdict,
      outcome_count: (updated[idx].outcome_count ?? 0) + 1,
      last_updated: today,
      source: "purchase_outcome",
    };
  } else {
    updated.push({
      brand: normalisedRetailer,
      verdict,
      category: isTop ? "tops" : "bottoms",
      source: "purchase_outcome",
      fit_reliability: reliability,
      outcome_count: 1,
      last_updated: today,
    });
  }

  await (supabase.from("profiles") as any).update({ [field]: updated }).eq("id", userId);
};

export default function PurchaseFeedbackCard({
  outfitId,
  itemName,
  itemCategory,
  retailer,
  productUrl,
  detectedItemId,
  onDismiss,
  onComplete,
}: Props) {
  const [step, setStep] = useState<Step>("purchased");
  const [didPurchase, setDidPurchase] = useState<boolean | null>(null);

  const savePurchaseOutcome = async (purchased: boolean | null, fitOutcome: string | null) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return;

    await (supabase.from("purchase_outcomes") as any).insert({
      user_id: userId,
      outfit_id: outfitId,
      detected_item_id: detectedItemId,
      item_name: itemName,
      item_category: itemCategory,
      retailer,
      product_url: productUrl,
      did_purchase: purchased,
      fit_outcome: fitOutcome,
      responded_at: new Date().toISOString(),
    });

    if (purchased && fitOutcome && retailer) {
      await updateBrandFit(userId, itemCategory, retailer, fitOutcome);
    }
  };

  const handlePurchased = async (val: "yes" | "no" | "thinking") => {
    if (val === "yes") {
      setDidPurchase(true);
      setStep("fit");
      return;
    }
    const purchased = val === "no" ? false : null;
    await savePurchaseOutcome(purchased, null);
    setStep("done");
    setTimeout(() => onComplete(), 2000);
  };

  const handleFit = async (outcome: string) => {
    await savePurchaseOutcome(didPurchase, outcome);

    if (outcome === "perfect") {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;
      if (userId) {
        void autoCaptureToWardrobe({
          userId,
          productUrl,
          productImage: null,
          productTitle: itemName,
          retailer,
          sizePurchased: null,
          category: itemCategory,
          detectedItemId,
        }).then((result) => {
          if (result.added && result.itemId) {
            const capturedId = result.itemId;
            toast.success("Added to your wardrobe", {
              action: {
                label: "Undo",
                onClick: () => {
                  void undoWardrobeAdd(capturedId).then((ok) => {
                    if (ok) toast("Removed from wardrobe");
                    else
                      toast.error(
                        "Couldn't undo — try removing from wardrobe",
                      );
                  });
                },
              },
              duration: 5000,
            });
          } else if (result.skipped_duplicate) {
            console.log("[auto-capture] item already in wardrobe");
          } else if (result.error) {
            console.warn("[auto-capture] failed:", result.error);
          }
        });
      }
    }

    setStep("done");
    setTimeout(() => onComplete(), 2000);
  };


  const pillBase: React.CSSProperties = {
    padding: "8px 14px",
    borderRadius: 999,
    border: `1px solid ${CORAL}`,
    background: "white",
    color: CORAL,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "var(--font-body)",
  };

  return (
    <section
      aria-label="Purchase feedback"
      className="mt-4"
      style={{
        borderRadius: 18,
        background: CREAM,
        border: "0.5px solid var(--border)",
        borderLeft: `4px solid ${CORAL}`,
        padding: 16,
        position: "relative",
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      }}
    >
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          position: "absolute",
          top: 10,
          right: 12,
          background: "transparent",
          border: "none",
          fontSize: 18,
          color: "var(--muted-foreground)",
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        ×
      </button>

      {step === "purchased" && (
        <>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: CORAL,
              marginBottom: 8,
            }}
          >
            Quick question
          </p>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 20,
              lineHeight: 1.2,
              color: "var(--l2f-ink)",
              margin: 0,
              marginBottom: 14,
              paddingRight: 24,
            }}
          >
            Did you buy the {itemName}?
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" style={pillBase} onClick={() => handlePurchased("yes")}>
              Yes
            </button>
            <button type="button" style={pillBase} onClick={() => handlePurchased("no")}>
              No, decided against it
            </button>
            <button type="button" style={pillBase} onClick={() => handlePurchased("thinking")}>
              Still thinking
            </button>
          </div>
          <button
            type="button"
            onClick={onDismiss}
            style={{
              marginTop: 12,
              background: "transparent",
              border: "none",
              padding: 0,
              fontSize: 12,
              color: "var(--muted-foreground)",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Skip for now
          </button>
        </>
      )}

      {step === "fit" && (
        <>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: CORAL,
              marginBottom: 8,
            }}
          >
            Quick question
          </p>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: 20,
              lineHeight: 1.2,
              color: "var(--l2f-ink)",
              margin: 0,
              marginBottom: 14,
              paddingRight: 24,
            }}
          >
            How did it fit?
          </h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" style={pillBase} onClick={() => handleFit("perfect")}>
              Perfect
            </button>
            <button type="button" style={pillBase} onClick={() => handleFit("too_tight")}>
              Slightly tight
            </button>
            <button type="button" style={pillBase} onClick={() => handleFit("too_loose")}>
              Too loose
            </button>
            <button type="button" style={pillBase} onClick={() => handleFit("returned")}>
              I returned it
            </button>
          </div>
        </>
      )}

      {step === "done" && (
        <p
          style={{
            fontSize: 14,
            color: "var(--l2f-ink)",
            margin: 0,
            paddingRight: 24,
          }}
        >
          Thanks — this helps us improve your recommendations
        </p>
      )}
    </section>
  );
}
